import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rateApiService, type RatePeriodPayload } from '@/services/rateApi.service';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/**
 * Changing a rate moves three things: the period list read from the Rate API, the
 * salary figure this app computed from it, and the backend's own cache of that
 * employee's periods.
 *
 * That third one is the easy one to forget. These writes go from the browser
 * straight to the Rate API, so the backend never observes them and keeps pricing
 * salary from cached periods until its TTL lapses. Since every salary read now
 * reprices the month, a stale cache means the next read bakes the OLD rate into the
 * stored record. Clearing it server-side is therefore not a cache nicety; it is what
 * stops a wrong number being persisted.
 *
 * It is fired first and awaited, so the refetches below — each of which triggers a
 * fresh server-side computation — cannot race ahead of it and re-read the stale rate.
 * A failure is surfaced rather than swallowed: the rate did save, so the write is not
 * lost, but salary will price at the old rate until the cache lapses, and the user
 * needs to know the figure on screen is not yet the new one.
 */
function useInvalidateRateQueries() {
  const queryClient = useQueryClient();

  return async (userId: string) => {
    try {
      await api.post(`/payroll/rate-cache/${encodeURIComponent(userId)}/invalidate`);
    } catch {
      toast.warning('Rate saved, but salary may show the old rate for a few minutes.');
    }

    void queryClient.invalidateQueries({ queryKey: ['rate-periods', userId] });
    void queryClient.invalidateQueries({ queryKey: ['payroll', 'salary', userId] });
  };
}

export function useCreateRatePeriod(userId: string, employeeName?: string) {
  const invalidate = useInvalidateRateQueries();

  return useMutation({
    mutationFn: (input: RatePeriodPayload) =>
      rateApiService.createPeriod(userId, input, employeeName),
    onSuccess: () => invalidate(userId),
  });
}

export function useUpdateRatePeriod(userId: string) {
  const invalidate = useInvalidateRateQueries();

  return useMutation({
    mutationFn: ({ rateId, input }: { rateId: number; input: Partial<RatePeriodPayload> }) =>
      rateApiService.updatePeriod(userId, rateId, input),
    onSuccess: () => invalidate(userId),
  });
}

export function useDeleteRatePeriod(userId: string) {
  const invalidate = useInvalidateRateQueries();

  return useMutation({
    mutationFn: (rateId: number) => rateApiService.deletePeriod(userId, rateId),
    onSuccess: () => invalidate(userId),
  });
}
